/*
The MIT License (MIT)

Copyright (c) 2013 Evan Wang

Permission is hereby granted, free of charge, to any person obtaining a copy
of this software and associated documentation files (the "Software"), to deal
in the Software without restriction, including without limitation the rights
to use, copy, modify, merge, publish, distribute, sublicense, and/or sell
copies of the Software, and to permit persons to whom the Software is
furnished to do so, subject to the following conditions:

The above copyright notice and this permission notice shall be included in all
copies or substantial portions of the Software.

THE SOFTWARE IS PROVIDED "AS IS", WITHOUT WARRANTY OF ANY KIND, EXPRESS OR
IMPLIED, INCLUDING BUT NOT LIMITED TO THE WARRANTIES OF MERCHANTABILITY,
FITNESS FOR A PARTICULAR PURPOSE AND NONINFRINGEMENT. IN NO EVENT SHALL THE
AUTHORS OR COPYRIGHT HOLDERS BE LIABLE FOR ANY CLAIM, DAMAGES OR OTHER
LIABILITY, WHETHER IN AN ACTION OF CONTRACT, TORT OR OTHERWISE, ARISING FROM,
OUT OF OR IN CONNECTION WITH THE SOFTWARE OR THE USE OR OTHER DEALINGS IN THE
SOFTWARE.
*/

export default class Labeler {
  constructor() {
    this.lab = [];
    this.anc = [];
    this.w = 1; // box width
    this.h = 1; // box width

    this.max_move = 2.0;
    this.max_angle = 0.5;
    this.acc = 0;
    this.rej = 0;

    // weights
    this.w_len = 1.0; // leader line length
    this.w_inter = 1.0; // leader line intersection
    this.w_lab2 = 30.0; // label-label overlap
    this.w_lab_anc = 0.0; // label-anchor overlap
    this.w_orient = 0.0; // orientation bias

    // booleans for user defined functions
    this.user_energy = false;
    this.user_schedule = false;
  }

  energy(index) {
      // energy function, tailored for label placement

      var m = this.lab.length;
      var ener = 0;
      var dx = this.lab[index].x - this.anc[index].x;
      var dy = this.anc[index].y - this.lab[index].y;
      var dist = Math.sqrt(dx * dx + dy * dy);
      var overlap = true;

      // penalty for length of leader line
      if (dist > 0) ener += dist * this.w_len;

      // label orientation bias
      dx /= dist;
      dy /= dist;
      if (dx > 0 && dy > 0) { ener += 0 * this.w_orient; }
      else if (dx < 0 && dy > 0) { ener += 1 * this.w_orient; }
      else if (dx < 0 && dy < 0) { ener += 2 * this.w_orient; }
      else { ener += 3 * this.w_orient; }

      var x21 = this.lab[index].x,
          y21 = this.lab[index].y - this.lab[index].height + 2.0,
          x22 = this.lab[index].x + this.lab[index].width,
          y22 = this.lab[index].y + 2.0;
      var x11, x12, y11, y12, x_overlap, y_overlap, overlap_area;

      for (var i = 0; i < m; i++) {
        if (i !== index) {
            // penalty for intersection of leader lines
            overlap = this.intersect(this.anc[index].x, this.lab[index].x, this.anc[i].x, this.lab[i].x,
                            this.anc[index].y, this.lab[index].y, this.anc[i].y, this.lab[i].y);
            if (overlap) ener += this.w_inter;

            // penalty for label-label overlap
            x11 = this.lab[i].x;
            y11 = this.lab[i].y - this.lab[i].height + 2.0;
            x12 = this.lab[i].x + this.lab[i].width;
            y12 = this.lab[i].y + 2.0;
            x_overlap = Math.max(0, Math.min(x12,x22) - Math.max(x11,x21));
            y_overlap = Math.max(0, Math.min(y12,y22) - Math.max(y11,y21));
            overlap_area = x_overlap * y_overlap;
            ener += (overlap_area * this.w_lab2);

          }

          // penalty for label-anchor overlap
          x11 = this.anc[i].x - this.anc[i].r;
          y11 = this.anc[i].y - this.anc[i].r;
          x12 = this.anc[i].x + this.anc[i].r;
          y12 = this.anc[i].y + this.anc[i].r;
          x_overlap = Math.max(0, Math.min(x12,x22) - Math.max(x11,x21));
          y_overlap = Math.max(0, Math.min(y12,y22) - Math.max(y11,y21));
          overlap_area = x_overlap * y_overlap;
          ener += (overlap_area * this.w_lab_anc);
      }
      return ener;
  }

  mcmove(currT) {
      // Monte Carlo translation move

      // select a random label
      var i = Math.floor(Math.random() * this.lab.length);

      // save old coordinates
      var x_old = this.lab[i].x;
      var y_old = this.lab[i].y;

      // old energy
      var old_energy;
      if (this.user_energy) {
        old_energy = this.user_defined_energy(i, this.lab, this.anc)
      }
      else {
        old_energy = this.energy(i)
      }

      // random translation
      //this.lab[i].x += (Math.random() - 0.5) * this.max_move;
      this.lab[i].y += (Math.random() - 0.5) * this.max_move;

      // hard wall boundaries
      // console.log("labeler width", this.h, this.lab[i].y, y_old);
      if (this.lab[i].x > this.w) this.lab[i].x = x_old;
      if (this.lab[i].x < 0)      this.lab[i].x = x_old;
      if (this.lab[i].y > this.h) this.lab[i].y = y_old;
      if (this.lab[i].y < 0)      this.lab[i].y = y_old;
      // console.log("labeler width2", this.lab[i].y);


      // new energy
      var new_energy;
      if (this.user_energy) {new_energy = this.user_defined_energy(i, this.lab, this.anc)}
      else {new_energy = this.energy(i)}

      // delta E
      var delta_energy = new_energy - old_energy;

      if (Math.random() < Math.exp(-delta_energy / currT)) {
        this.acc += 1;
      } else {
        // move back to old coordinates
        this.lab[i].x = x_old;
        this.lab[i].y = y_old;
        this.rej += 1;
      }

  }

  mcrotate(currT) {
  // Monte Carlo rotation move

      // select a random label
      var i = Math.floor(Math.random() * this.lab.length);

      // save old coordinates
      var x_old = this.lab[i].x;
      var y_old = this.lab[i].y;

      // old energy
      var old_energy;
      if (this.user_energy) {old_energy = this.user_defined_energy(i, this.lab, this.anc)}
      else {old_energy = this.energy(i)}

      // random angle
      //var angle = (Math.random() - 0.5) * this.max_angle;

      var movement = Math.random() - 0.5;

      // translate label back
      this.lab[i].x += movement;

      // hard wall boundaries
      if (this.lab[i].x > this.w) this.lab[i].x = x_old;
      if (this.lab[i].x < 0) this.lab[i].x = x_old;
      if (this.lab[i].y > this.h) this.lab[i].y = y_old;
      if (this.lab[i].y < 0) this.lab[i].y = y_old;

      // new energy
      var new_energy;
      if (this.user_energy) {new_energy = this.user_defined_energy(i, this.lab, this.anc)}
      else {new_energy = this.energy(i)}

      // delta E
      var delta_energy = new_energy - old_energy;

      if (Math.random() < Math.exp(-delta_energy / currT)) {
        this.acc += 1;
      } else {
        // move back to old coordinates
        this.lab[i].x = x_old;
        this.lab[i].y = y_old;
        this.rej += 1;
      }

  }

  intersect(x1, x2, x3, x4, y1, y2, y3, y4) {
    // returns true if two lines intersect, else false
    // from http://paulbourke.net/geometry/lineline2d/

    var mua, mub;
    var denom, numera, numerb;

    denom = (y4 - y3) * (x2 - x1) - (x4 - x3) * (y2 - y1);
    numera = (x4 - x3) * (y1 - y3) - (y4 - y3) * (x1 - x3);
    numerb = (x2 - x1) * (y1 - y3) - (y2 - y1) * (x1 - x3);

    /* Is the intersection along the the segments */
    mua = numera / denom;
    mub = numerb / denom;
    if (!(mua < 0 || mua > 1 || mub < 0 || mub > 1)) {
        return true;
    }
    return false;
  }

  cooling_schedule(currT, initialT, nsweeps) {
  // linear cooling
    return (currT - (initialT / nsweeps));
  }

  start(nsweeps) {
    // main simulated annealing function
    var m = this.lab.length,
        currT = 1.0,
        initialT = 1.0;

    for (var i = 0; i < nsweeps; i++) {
      for (var j = 0; j < m; j++) {
        this.mcmove(currT);
      }
      currT = this.cooling_schedule(currT, initialT, nsweeps);
    }
  }

  width(x) {
    // users insert graph width
    if (!arguments.length) return this.w;
    this.w = x;
    return this;
  }

  height = function(x) {
    // users insert graph height
    if (!arguments.length) return this.h;
    this.h = x;
    return this;
  }

  label(x) {
    // users insert label positions
    if (!arguments.length) return this.lab;
    this.lab = x;
    return this;
  }

  anchor(x) {
    // users insert anchor positions
    if (!arguments.length) return this.anc;
    this.anc = x;
    return this;
  }

  alt_energy(x) {
  // user defined energy
    if (!arguments.length) return this.energy;
    this.user_defined_energy = x;
    this.user_energy = true;
    return this;
  }

  alt_schedule(x) {
    // user defined cooling_schedule
    if (!arguments.length) return  this.cooling_schedule;
    this.user_defined_schedule = x;
    this.user_schedule = true;
    return this;
  }
};



// WEBPACK FOOTER //
// ./src/Labeler.js